
import AppMobileFullScreenContainer from "@/components/common/MobileFullScreenContainer.vue";
import Vue from "vue";
import { DefaultEditTab, getVisibleDefaultEditTabs } from "@/utils/tabs/default-edit-tabs";
import {Purchase} from "@/models/base";
import { PurchaseService } from "../../services/purchase-service";
import AppDefault from "./PurchaseEditDefault.vue";
import AppPurchaseDocuments from "./PurchaseDocuments.vue";
import AppSelectContact from "./PurchaseEditSelectContact.vue";
import AppPurchaseDocumentSearchBrowse from "./PurchaseDocumentSearchBrowse.vue";
import AppEditVat from "./PurchaseEditVat.vue";
import { BankService } from "@/services/bank-service";
import AppPurchaseEditLedgersCard from "./PurchaseEditLedgersCard.vue";
import { AuthService } from "@/services/auth-service";
import { MODULE } from "@/config/modules";
import { PurchaseLedger } from "@/models/base/purchase-ledger";
import { LedgerAccountVatField } from "@/models/ledger-account";
import { printHumanFloat } from "@/utils/pretty-print";
import { HttpError } from "@/utils/services/http-error";
import {BookYearService, DossierConfigService, UserConfigService} from "@/services";
import AppExtraFieldTab from "@/components/ExtraFieldTab.vue";
import { ExtraFieldTabConfig } from "@/models/dossier-configs/extra-field-tab-config";
import { ExtraFieldNamesConfig } from "@/models/dossier-configs/extra-field-names-config";
import { isPurchaseVatRegimeStaticVat, PurchaseVatRegime } from "@/services/vat-service";
import AppPurchaseEditExtraFields from "./PurchaseEditExtraFields.vue";
import {roundPriceValues} from "@/utils/numbers";
import {PurchaseAwsFile} from "@/models/base/purchase-aws-file";
import AppBankEdit from "@/components/bank/QuickEdit.vue";
import {CurrencyService} from "@/services/currency-service";
import Currency from "@/models/base/currency";
import { ConfirmResult } from "winfakt-vue-components";

interface IFileWrapper {
	key:string;
	url:string;
	file?:File;
	fileName:string;
	awsFile?:PurchaseAwsFile;
}

export default Vue.extend({
	data() {
		return {
			openQuickBankEdit: false,
			quickBankEditPurchase: null as null | Purchase,
			editLedgersOpen: false,
			hasFilledDefaultLedger: false,
			fetchedFriendlyId: {
				computedFriendlyId: "",
				friendlyId: 0,
				journalId: 0
			},
			savedFriendlyId: {
				computedFriendlyId: "",
				friendlyId: 0,
				journalId: 0
			},
			tab: 0,
			config: UserConfigService.getSaleSettings(),
			fileList: [] as IFileWrapper[],
			expand: 0
		};
	},
	props: {
		value: {
			type: Object as () => Purchase,
			required: true
		},
		saveDisabled: Boolean
	},
	computed: {
		canCreateFixedAsset():boolean {
			return AuthService.isDevModeEnabled && AuthService.hasModule(MODULE.AFSCHRIJVINGSTABELLEN) && this.value.ID != 0 && this.value.VATBuisinessAssets != 0;
		},
		hasLedgerModule():boolean {
			// Was vroeger enkel BTW-aangifte. Maar in de offline was het de module boekhoudlijsten. Daarom worden beiden aanvaard
			return AuthService.hasModule(MODULE.BTW_AANGIFTE_EN_LISTING) || AuthService.hasModule(MODULE.BOEKHOUDLIJSTEN);
		},
		defaultTabs(): DefaultEditTab[] {
			return getVisibleDefaultEditTabs(9, tab => tab.key != "purchase-validation" || this.value.ContactID > 0);
		},
		currentTabKey(): string {
			return this.defaultTabs[this.tab]?.key || "";
		},
		tabs():string[]{
			return [...this.defaultTabs.map(t=>t.label()), ...this.extraFieldNamesConfig.getTabNames()];
		},
		extraFieldNamesConfig():ExtraFieldNamesConfig {
			return DossierConfigService.getExtraFieldNamesConfigs().PurchaseNames;
		},
		extraFieldTabs():ExtraFieldTabConfig[] {
			return this.extraFieldNamesConfig.getTabs();
		},
		extraFieldTabStart():number {
			return this.defaultTabs.length;
		},
		ready():boolean {
			return true;
		},
	},
	watch: {
		"value": {
			immediate: true,
			handler(){
				if (this.value.ID == 0) return;
				this.savedFriendlyId.computedFriendlyId = this.value.ComputedFriendlyID;
				this.savedFriendlyId.friendlyId = this.value.FriendlyID;
				this.savedFriendlyId.journalId = this.value.JournalID;
			}
		},
		"value.Contact"() {
			if (!this.value.ID) {
				this.updateJournal();
				this.calculateCurrencyRate();
			}
			this.checkAndFillVatsByLedger();
		},
		"value.Total"(){
			this.checkAndFillVatsByLedger();
		},
		"value.DiscountPercantage"(){
			this.checkAndFillVatsByLedger();
		},
		"value.JournalID": {
			immediate: true,
			handler(){
				this.setNewFriendlyID();
			}
		},
		"value.IsCreditNote": {
			handler(){
				this.checkAndFillVatsByLedger();
			}
		},
	},
	methods: {
		updatePaymentPercentage(amount:number){
			let basis = ((this.value.Total-amount) / (1+(this.value.getVat()))) + amount;
			this.value.DiscountPercantage = (amount/basis)*100;
			this.checkAndFillVatsByLedger();
		},
		calculateCurrencyRate(){
			let currencies = CurrencyService.getCurrencies();
			if (currencies.length == 0){
				return 1;
			}
			let foundCurrency = currencies.filter((c:Currency) => c.ID == this.value.CurrencyID)[0];
			if (!foundCurrency) return 1;
			this.value.CurrencyRate = foundCurrency.Rate || 1;
		},
		// eslint-disable-next-line max-lines-per-function
		checkAndFillVatsByLedger(){
			if (!this.value.Contact) return;
			if (this.value.Total == 0) return;
			let netto = roundPriceValues(this.value.Total / (1 + this.value.getVat()));

			if(this.value.DiscountPercantage){
				let divider = (1+(this.value.getVat())*((100-this.value.DiscountPercantage)/100));
				netto = roundPriceValues(this.value.Total / divider);
			}

			let vat = this.value.Total - netto;
			let altVatComputationList = [PurchaseVatRegime.AANKOOP_UIT_ANDERE_LIDSTATEN_BINNEN_DE_EG, PurchaseVatRegime.BTW_MEDECONTRACTANT];
			if (altVatComputationList.indexOf(this.value.Contact.VatRegime) != -1){
				netto = this.value.Total;
				vat = this.value.Total * this.value.getVat();
				if (this.value.Total < 0 && PurchaseVatRegime.AANKOOP_UIT_ANDERE_LIDSTATEN_BINNEN_DE_EG) {
					vat = 0;
				}
			}
			vat = roundPriceValues(vat);
			if (this.value.Contact.VatRegime == PurchaseVatRegime.AANKOOP_BUITEN_DE_EG) {
				vat = roundPriceValues(this.value.Total * this.value.getVat());
				netto = this.value.Total - vat;
			}
			this.value.VATAmountCoContracts = 0;
			this.value.VATCoContracts = 0;
			this.value.VATAmountImportWithinTheEG = 0;
			this.value.VATImportWithinTheEG = 0;
			this.value.VATImportOutsideTheEG = 0;
			this.checkAndFillVatsByLedgerCheckDefaultLedgerAccount(netto);
			this.value.VATSubtractable = vat;
			this.value.VATNotSubtractable = 0;
			if (this.value.Total < 0 && this.value.isInternationalVatRegime()) {
				vat = 0;
			}
			if (this.value.Contact.VatRegime == PurchaseVatRegime.VRIJ_VAN_BTW || this.value.Contact.VatRegime == PurchaseVatRegime.VRIJ_VAN_BTW_CLASSIC) {
				this.value.VATSubtractable = 0;
			}else if (this.value.Contact.VatRegime == PurchaseVatRegime.BTW_MEDECONTRACTANT){
				this.value.VATAmountCoContracts = netto;
				this.value.VATCoContracts = vat;
			}else if (this.value.Contact.VatRegime == PurchaseVatRegime.AANKOOP_UIT_ANDERE_LIDSTATEN_BINNEN_DE_EG) {
				this.value.VATAmountImportWithinTheEG = netto;
				this.value.VATImportWithinTheEG = Math.max(vat, 0);
				this.value.VATSubtractable = Math.max(this.value.VATSubtractable, 0);
			}else if (this.value.Contact.VatRegime == PurchaseVatRegime.AANKOOP_BUITEN_DE_EG) {
				this.value.VATImportOutsideTheEG = vat;
				this.value.VATAmountCoContracts = this.value.Total;
				this.value.VATCoContracts = 0;
			}
		},
		checkAndFillVatsByLedgerCheckDefaultLedgerAccount(netto:number){
			if (!this.value.Contact) return;
			if (this.value.Contact.VatRegime == 4) {
				netto = this.value.Total;
			}
			netto = roundPriceValues(netto);
			this.value.VATPurchaseTradingGoods = 0;
			this.value.VATServicesAndCosts = 0;
			this.value.VATBuisinessAssets = 0;
			if (!this.value.Contact.DefaultLedgerAccount) {
				this.value.VATServicesAndCosts = netto;
				return;
			}
			if (this.value.Contact.DefaultLedgerAccount.VatField == LedgerAccountVatField.AANKOPEN_HANDELSGOEDEREN) {
				this.value.VATPurchaseTradingGoods = netto;
			}else if (this.value.Contact.DefaultLedgerAccount.VatField == LedgerAccountVatField.DIENSTEN_OF_KOSTEN) {
				this.value.VATServicesAndCosts = netto;
			}else if (this.value.Contact.DefaultLedgerAccount.VatField == LedgerAccountVatField.BEDRIJFSMIDDELEN){
				this.value.VATBuisinessAssets = netto;
			}else if (this.value.Contact.DefaultLedgerAccountCode.startsWith("60")){
				this.value.VATPurchaseTradingGoods = netto;
			}else if (this.value.Contact.DefaultLedgerAccountCode.startsWith("2")){
				this.value.VATBuisinessAssets = netto;
			}else{
				this.value.VATServicesAndCosts = netto;
			}
		},
		async checkIfComputedFriendlyIDExists() {
			try {
				let foundPurchase = await PurchaseService.getByFriendlyId(this.value.ComputedFriendlyID);
				if (foundPurchase) {
					if (foundPurchase.ID == this.value.ID) {
						return false;
					}
					return true;
				}
				return false;
			} catch (err) {
				let error = err as HttpError;
				if (error.status == 404) {
					error.dontShow = true;
					return false;
				}
			}
			return false;
		},
		validateCheckTotalBookedEqualsTotal():boolean {
			let totalBooked = this.value.getTotalBooked();
			let total = this.value.Total;
			if (AuthService.hasModule(MODULE.BOEKHOUDLIJSTEN)) {
				if (!this.value.Contact) return false;
				if (this.value.Contact.VatRegime == PurchaseVatRegime.BTW_MEDECONTRACTANT) {
					if (!this.value.Contact.DefaultLedgerAccountCode && this.value.Ledgers.length == 0) {
						let text = this.$ct("no-ledger-accounts-selected");
						this.$wf.dialog(text, this.$ct("common.ok"), this.$ct("common.warning"), "warning");
						return false;
					}
				}
			}
			if(AuthService.hasModule(MODULE.BOEKHOUDLIJSTEN)){
				if (Math.abs(totalBooked - total) >= 0.009){
					let text = this.$ct("error-total-booked-is-not-equal-to-total", {booked: printHumanFloat(totalBooked), total: printHumanFloat(total)});
					this.$wf.dialog(text, this.$ct("common.ok"), this.$ct("common.warning"), "warning");
					return false;
				}
				let vatTotal = this.value.VATPurchaseTradingGoods + this.value.VATServicesAndCosts + this.value.VATBuisinessAssets;
				if (this.hasLedgerModule && Math.abs(vatTotal - this.value.getLedgersTotal()) >= 0.009){
					let text = this.$ct("error-total-vat-is-not-equal-to-total-booked", {vats: printHumanFloat(vatTotal), booked: printHumanFloat(this.value.getLedgersTotal())});
					this.$wf.dialog(text, this.$ct("common.ok"), this.$ct("common.warning"), "warning");
					return false;
				}
			}

			return true;
		},
		handleError(err:any){
			let error = err as HttpError;
			if (error.data[0] == "LedgerCode is not a valid ledger code"){
				error.dontShow = true;
				let message = this.$ct("error-incorrect-ledger-account-in-ledgers").toString();
				this.$wf.notify(message, "danger", 3000, "exclemation-triangle");
			}
		},
		async shouldContinueIfPurchaseExists(p:Purchase):Promise<boolean>{
			let possibleDuplicate = await PurchaseService.findPossibleDuplicatePurchase(p);
			if (!possibleDuplicate) return true;
			let text = this.$ct("components.purchases.this-document-is-a-possible-duplicate-of-number", {
				documentId: possibleDuplicate.ID,
				documentNr: possibleDuplicate.ComputedFriendlyID
			});
			console.log(text);
			let confirm = await this.$wf.confirm(text.toString(), this.$ct("common.go-further"), this.$ct("common.cancel"), this.$ct("common.warning"), "warning");
			return confirm == ConfirmResult.YES;
		},
		// eslint-disable-next-line max-lines-per-function
		async save():Promise<boolean> {
			if (this.saveDisabled) return false;
			let supposedBookyear = BookYearService.getBookYearFromDate(this.value.InvoiceDate);
			if (supposedBookyear != BookYearService.selectedBookYear) {
				let confirm = await this.$confirmBuilder().setWarningTitle().setMessage(this.$ct("confirm-wrong-bookyear")).show();
				if (!confirm) return false;
			}
			let vatPeriodAsDate = new Date();
			vatPeriodAsDate.setDate(1);
			vatPeriodAsDate.setMonth(this.value.VatPeriod-1);
			vatPeriodAsDate.setFullYear(this.value.VatYear);
			supposedBookyear = BookYearService.getBookYearFromDate(vatPeriodAsDate);
			if (supposedBookyear != BookYearService.selectedBookYear) {
				let confirm = await this.$confirmBuilder().setWarningTitle().setMessage(this.$ct("confirm-wrong-bookyear-for-vat-period")).show();
				if (!confirm) return false;
			}


			if (await this.checkIfComputedFriendlyIDExists()) {
				let message = this.$ct("components.sales.sale-friendly-id-already-in-use");
				this.$wf.dialog(message, this.$ct("common.ok"), this.$ct("common.save-failed"));
				return false;
			}

			this.fillDefaultLedger();
			if (this.value.ContactID == 0) {
				this.$wf.notify(this.$ct("error-missing-contact"), "danger", 3000, "exclamation-triangle");
				return false;
			}
			if (!this.validateCheckTotalBookedEqualsTotal()) return false;
			try{
				if (this.value.ID == 0) {
					let shouldContinue = await this.shouldContinueIfPurchaseExists(this.value);
					if (!shouldContinue) return false;
					// Prevent incorrect numbering
					if (this.fetchedFriendlyId.computedFriendlyId != this.value.ComputedFriendlyID) {
						this.value.FriendlyID = 0;
					}
					var result = await PurchaseService.postPurchase(this.value);
				} else {
					let originalPurchase = await PurchaseService.getPurchase(this.value.ID);
					if (this.value.willBanksBeDeletedOnSave(originalPurchase)){
						let banks = await BankService.getBookingsByPurchase(this.value);
						if (banks.length != 0){
							let confirm = await this.$confirmBuilder().setWarningTitle().setMessage(this.$ct("confirm-delete-banks")).show();
							if (!confirm) return false;
						}
					}

					var result = await PurchaseService.putPurchase(this.value);
				}
			}catch(err){
				this.handleError(err);
				throw err;
			}
			this.value.ID = result.ID;
			let savedPurchase = await PurchaseService.getPurchase(result.ID);
			if(this.openQuickBankEdit){
				this.quickBankEditPurchase = savedPurchase;
			}else{
				this.$emit("input", savedPurchase);
			}
			return true;
		},
		cancel() {
			this.$emit("cancel");
		},
		async remove() {
			let confirm = await this.$wf.confirm(this.$ct("delete-confirm"), this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.warning"), "warning");
			if (!confirm) return;
			await PurchaseService.delPurchases([this.value]);
			this.$emit("cancel");
		},
		updateJournal() {
			if (this.value.Contact) {
				this.value.JournalID = this.value.Contact.DefaultJournalID;
			}
		},
		openLedgers(){
			this.fillDefaultLedger();
			this.editLedgersOpen = true;
		},
		createFixedAsset(){
			this.$router.push({path: "/fixed-assets/new", query: {purchaseId: `${this.value.ID}`, bookYear: `${this.value.BookYear}`}});
		},
		fillDefaultLedger(){
			if (!this.hasLedgerModule) return;
			if (!this.value.Contact) return;
			if (!this.value.Contact.DefaultLedgerAccountCode) return;
			if (this.hasFilledDefaultLedger) return;
			if (this.value.ID != 0) return;
			if (this.value.Ledgers.length != 0) return;
			let ledger = new PurchaseLedger();
			ledger.LedgerCode = this.value.Contact.DefaultLedgerAccountCode;
			ledger.Amount = this.value.VATPurchaseTradingGoods + this.value.VATServicesAndCosts + this.value.VATBuisinessAssets;
			if (isPurchaseVatRegimeStaticVat(this.value.getVatRegime())){
				ledger.VatPercent = this.value.getVat() * 100;
			}else{
				let vat = this.value.getTotalVat() - this.value.VATSubtractable;
				ledger.VatPercent = ((this.value.Total / (this.value.Total - vat)) - 1) * 100;
			}
			this.value.Ledgers.push(ledger);
			this.hasFilledDefaultLedger = true;
		},
		async setNewFriendlyID(){
			if (this.value.ID) {
				if (this.value.JournalID == this.savedFriendlyId.journalId){
					this.value.ComputedFriendlyID = this.savedFriendlyId.computedFriendlyId;
					this.value.FriendlyID = this.savedFriendlyId.friendlyId;
					return;
				}
			}
			let journalId = this.value.JournalID;
			let res = await PurchaseService.getNextAvailableComputedID(journalId);
			if (journalId != this.value.JournalID) return;
			this.fetchedFriendlyId.computedFriendlyId = res.computedFriendlyId;
			this.fetchedFriendlyId.friendlyId = res.friendlyId;
			this.fetchedFriendlyId.journalId = journalId;
			this.value.ComputedFriendlyID = res.computedFriendlyId;
			this.value.FriendlyID = res.friendlyId;
		},
		onBankSave(){
			this.$emit("input",this.quickBankEditPurchase);
		}
	},
	destroyed(){
		for (let file of this.fileList){
			if (file.url && file.file){
				URL.revokeObjectURL(file.url);
			}
		}
	},
	components: {
		AppDefault,
		AppEditVat,
		AppSelectContact,
		AppMobileFullScreenContainer,
		AppPurchaseEditLedgersCard,
		AppPurchaseEditExtraFields,
		AppBankEdit,
		AppPurchaseDocumentSearchBrowse,
		AppPurchaseDocuments,
		AppExtraFieldTab
	},
	i18nEx: {
		componentPrefix: "components.purchases"
	}
});
